import { createBattle, hashState, snapshot, stepBattle, type BattleParams, type BattleState, type Command, type TickInputs } from '@somnhold/sim';
import { z } from 'zod';
import { BattleCommandSchema } from './schemas.ts';

/**
 * Lockstep for live play (SPEC §10.3). The server is the clock: every 83 ms it closes a tick and
 * broadcasts the inputs of every player for it. Clients send their commands for `tick + INPUT_DELAY`.
 * Nobody ever waits for a slow player: inputs that arrive late are moved to the next tick the server
 * closes. Both sides run the same deterministic simulation; hashes are compared every 60 ticks.
 */

export const INPUT_DELAY = 2;
export const HASH_EVERY = 60;
/**
 * A page whose hashes come this many ticks behind the room (4 s, round trip included) can't keep up
 * stepping every tick: it gets the whole state instead, at most once every CATCHUP_EVERY ms.
 */
const FAR_BEHIND = 48;
const CATCHUP_EVERY = 10_000;
/** How far ahead of the room an input may be scheduled (a few round trips' worth). */
const MAX_AHEAD = 36;
/** A command a live player may send: a battle command, or setting out from the lobby. */
const LiveCommandSchema = z.union([BattleCommandSchema, z.object({ t: z.literal('start') }).strict()]);
/**
 * Without any message for this long, a player's hero turns into its ghost. Clients ping every second;
 * a few seconds of slack so one hitch on the network (or the server) doesn't drop everyone at once.
 */
export const DROP_AFTER_MS = 8000;
/**
 * What a player's pings said over one line of the room's lag log: round trip, frame rate, the longest frame
 * (`jankMax`, and `janks`: seconds with one over JANK_MS) and the longest wait between ticks as the page saw
 * them (`gapMax`, and `gaps`: seconds with one over LATE_MS). A long frame is the page; a long gap with short
 * frames is the network.
 */
export interface NetLine {
  rtt: number;
  rttMin: number;
  rttMax: number;
  fps: number;
  fpsMin: number;
  pings: number;
  jankMax: number;
  janks: number;
  gapMax: number;
  gaps: number;
}
const JANK_MS = 100;
const LATE_MS = 250;
/** Live heroes in one room: the whole group can fight a wave together. */
export const MAX_LIVE = 8;
/** Chat in a live room: outside the lockstep (it never touches the battle), short lines, a little flood control. */
export const CHAT = { maxLength: 140, minGapMs: 700, keep: 30 };

/**
 * Signals in a live room ("attack here", "defend here", "watch out", "on my way", "help!"): like the chat,
 * outside the lockstep and never kept. A few in a row, then a short wait, so a frantic hand can't flood the screen.
 */
export const SIGNAL = { burst: 3, windowMs: 4000 };
export const SIGNAL_KINDS = ['attack', 'defend', 'danger', 'coming', 'help'] as const;
export type SignalKind = (typeof SIGNAL_KINDS)[number];
/** A spot on the board (sim units) and, when it was put on someone (a nightmare, a hero), whom it follows. */
export interface Signal {
  kind: SignalKind;
  x: number;
  y: number;
  target?: number;
}
export interface SignalLine extends Signal {
  pid: string;
  at: number;
}

export interface ChatLine {
  pid: string;
  text: string;
  at: number;
}

export type ClientMsg =
  | { t: 'join'; pid: string; sector: number }
  | { t: 'input'; tick: number; cmds: Command[] }
  | { t: 'hash'; tick: number; hash: string }
  /** Every second; carries what the player sees (round trip, frame rate) for the room's lag log. */
  | { t: 'ping'; at: number; rtt?: number; fps?: number; /** Longest frame since the last ping, ms. */ jank?: number; /** Longest wait between ticks since the last ping, ms. */ gap?: number }
  | { t: 'chat'; text: string }
  | ({ t: 'signal' } & Signal)
  | { t: 'leave' };

export type ServerMsg =
  /** First on every live socket: the deploy (commit) the campaign's object runs, which may be older than the pages served now (docs/DEPLOY.md). */
  | { t: 'build'; build: string }
  | { t: 'welcome'; snapshot: BattleState; tick: number; roster: string[]; you: string }
  | { t: 'tick'; tick: number; inputs: TickInputs }
  | { t: 'desync'; tick: number }
  | { t: 'resync'; snapshot: BattleState; tick: number }
  | { t: 'roster'; players: string[]; ghosts: string[] }
  | { t: 'pong'; at: number }
  | ({ t: 'chat' } & ChatLine)
  | ({ t: 'signal' } & SignalLine)
  | { t: 'full' }
  /** The room is gone: the one who opened it left before setting out, or the fight broke down. */
  | { t: 'cancelled' }
  /** The same player connected again (another tab, a reconnect): this connection is closed, don't come back on it. */
  | { t: 'replaced' }
  | { t: 'end'; result: 'won' | 'lost'; tick: number; snapshot?: BattleState };

interface Conn {
  /** When it was last sent the whole state for falling far behind. */
  caughtUp?: number;
  pid: string;
  lastSeen: number;
  connected: boolean;
  live: boolean;
}

/** Authoritative side, transport-agnostic. `send` delivers to one connection id. */
export class LockstepServer {
  battle: BattleState;
  private pending = new Map<number, Map<string, Command[]>>();
  private conns = new Map<string, Conn>();
  private hashes = new Map<number, string>();
  private injected: TickInputs = {};
  /** Connections a reconnecting player replaced (told so): the room closes their sockets. */
  readonly replaced: string[] = [];
  /** Ticks per connection whose hash did not match (diagnostics; must stay empty; the room's lag line reads and empties it). */
  readonly desyncs: { pid: string; tick: number }[] = [];
  /** Full input history, for the replay. */
  readonly history: { tick: number; inputs: TickInputs }[] = [];
  /** Round trip and frame rate each player reported since the room's lag line last read them (diagnostics only, never in the battle). */
  readonly net = new Map<string, NetLine>();
  /** Recent chat, for whoever joins later. */
  private chat: ChatLine[] = [];
  private lastChat = new Map<string, number>();
  /** When each player's recent signals went out (flood control). */
  private signalled = new Map<string, number[]>();

  /** What was said so far (a room rebuilt for a bigger party carries it over with `keepChat`). */
  get chatLog(): ChatLine[] {
    return this.chat.slice();
  }

  keepChat(lines: ChatLine[]): void {
    this.chat = lines.slice(-CHAT.keep);
  }

  constructor(
    params: BattleParams,
    private send: (connId: string, msg: ServerMsg) => void,
    /** Players allowed to join (hero in the battle). */
    private allowed: (pid: string) => boolean = () => true,
    /** Lobby: the fight waits for `begin()` (the wave's hour); heroes can already walk around. */
    lobby = false,
  ) {
    this.battle = createBattle(params);
    if (lobby) this.battle.started = false;
    // Everyone starts as a ghost until they connect (in an expedition, a bot-played hero stays one: dev only).
    for (const h of this.battle.heroes) if (!(h.control === 'merc' && this.battle.dungeon?.exp)) h.control = 'ghost';
  }

  /**
   * Picks up a fight saved before the room was lost (the campaign's object restarted): its state and the
   * inputs so far, so the replay still runs from the start. Everyone is a ghost until they connect again.
   */
  resume(battle: BattleState, history: { tick: number; inputs: TickInputs }[]): void {
    this.battle = battle;
    this.history.length = 0;
    this.history.push(...history);
    for (const h of this.battle.heroes) if (h.control === 'live') h.control = 'ghost';
  }

  /** Starts the fight in the next closed tick (injected so every client starts on the same tick). */
  begin(): void {
    if (this.battle.started) return;
    const any = this.battle.heroes[0]?.pid ?? '_';
    this.injected[any] = [...(this.injected[any] ?? []), { t: 'start' }];
  }

  get started(): boolean {
    return this.battle.started;
  }

  connected(): number {
    return [...this.conns.values()].filter((c) => c.connected).length;
  }

  /** Connection ids that went silent (the transport should close them). */
  silent(now: number): string[] {
    return [...this.conns.entries()].filter(([, c]) => c.connected && now - c.lastSeen > DROP_AFTER_MS).map(([id]) => id);
  }

  get tick(): number {
    return this.battle.tick;
  }

  roster(): { players: string[]; ghosts: string[] } {
    const players = [...this.conns.values()].filter((c) => c.connected).map((c) => c.pid).sort();
    const ghosts = this.battle.heroes.filter((h) => h.control !== 'live').map((h) => h.pid);
    return { players, ghosts };
  }

  private broadcast(msg: ServerMsg): void {
    for (const [id, c] of this.conns) if (c.connected) this.send(id, msg);
  }

  onMessage(connId: string, msg: ClientMsg, now: number): void {
    let c = this.conns.get(connId);
    if (msg.t === 'join') {
      if (!this.allowed(msg.pid)) return;
      const liveCount = [...this.conns.values()].filter((x) => x.connected && x.pid !== msg.pid).length;
      if (liveCount >= MAX_LIVE) {
        this.send(connId, { t: 'full' });
        return;
      }
      // A reconnecting player replaces their old connection.
      for (const [id, other] of this.conns) {
        if (other.pid !== msg.pid || id === connId) continue;
        this.send(id, { t: 'replaced' });
        this.conns.delete(id);
        this.replaced.push(id);
      }
      c = { pid: msg.pid, lastSeen: now, connected: true, live: false };
      this.conns.set(connId, c);
      this.send(connId, { t: 'welcome', snapshot: snapshot(this.battle), tick: this.battle.tick, roster: this.roster().players, you: msg.pid });
      for (const line of this.chat) this.send(connId, { t: 'chat', ...line });
      this.control(msg.pid, 'live');
      c.live = true;
      this.broadcast({ t: 'roster', ...this.roster() });
      return;
    }
    if (!c || !c.connected) return;
    c.lastSeen = now;
    switch (msg.t) {
      case 'input': {
        // Whatever a page sends is checked: an input with no list of commands, or no tick, is dropped.
        if (!Array.isArray(msg.cmds) || typeof msg.tick !== 'number' || !Number.isFinite(msg.tick)) break;
        // Late inputs go to the next tick the server will close; never stall the others. One far ahead (a
        // page that lost track of time) lands soon instead of waiting, in memory, for a tick that never comes.
        const at = msg.tick <= this.battle.tick ? this.battle.tick + 1 : Math.min(Math.floor(msg.tick), this.battle.tick + MAX_AHEAD);
        const byTick = this.pending.get(at) ?? new Map<string, Command[]>();
        // Only commands of a known shape reach the simulation (one from an older page would throw in it,
        // and a fight that throws is called off for everyone).
        const cmds = msg.cmds.slice(0, 8).filter((x) => LiveCommandSchema.safeParse(x).success) as Command[];
        if (!cmds.length) break;
        byTick.set(c.pid, [...(byTick.get(c.pid) ?? []), ...cmds]);
        this.pending.set(at, byTick);
        break;
      }
      case 'hash': {
        if (typeof msg.tick === 'number' && this.battle.tick - msg.tick > FAR_BEHIND && now - (c.caughtUp ?? -Infinity) > CATCHUP_EVERY) {
          // Far behind (a slow phone, a page that was busy): jump it to the present instead of letting it
          // grind through every tick in between.
          c.caughtUp = now;
          this.send(connId, { t: 'resync', snapshot: snapshot(this.battle), tick: this.battle.tick });
          break;
        }
        const mine = this.hashes.get(msg.tick);
        if (mine && mine !== msg.hash) {
          this.desyncs.push({ pid: c.pid, tick: msg.tick });
          this.send(connId, { t: 'desync', tick: msg.tick });
          this.send(connId, { t: 'resync', snapshot: snapshot(this.battle), tick: this.battle.tick });
        }
        break;
      }
      case 'ping':
        this.send(connId, { t: 'pong', at: msg.at });
        if (typeof msg.rtt === 'number' && typeof msg.fps === 'number' && msg.rtt > 0) {
          const rtt = Math.min(Math.round(msg.rtt), 60_000);
          const fps = Math.min(Math.round(msg.fps), 1000);
          // Older pages send neither: 0.
          const jank = typeof msg.jank === 'number' ? Math.min(Math.round(msg.jank), 60_000) : 0;
          const gap = typeof msg.gap === 'number' ? Math.min(Math.round(msg.gap), 60_000) : 0;
          const n = this.net.get(c.pid);
          this.net.set(
            c.pid,
            n
              ? { rtt, rttMin: Math.min(n.rttMin, rtt), rttMax: Math.max(n.rttMax, rtt), fps, fpsMin: Math.min(n.fpsMin, fps), pings: n.pings + 1, jankMax: Math.max(n.jankMax, jank), janks: n.janks + (jank > JANK_MS ? 1 : 0), gapMax: Math.max(n.gapMax, gap), gaps: n.gaps + (gap > LATE_MS ? 1 : 0) }
              : { rtt, rttMin: rtt, rttMax: rtt, fps, fpsMin: fps, pings: 1, jankMax: jank, janks: jank > JANK_MS ? 1 : 0, gapMax: gap, gaps: gap > LATE_MS ? 1 : 0 },
          );
        }
        break;
      case 'chat': {
        const text = typeof msg.text === 'string' ? msg.text.replace(/\s+/g, ' ').trim().slice(0, CHAT.maxLength) : '';
        if (!text || now - (this.lastChat.get(c.pid) ?? -Infinity) < CHAT.minGapMs) break;
        this.lastChat.set(c.pid, now);
        const line = { pid: c.pid, text, at: now };
        this.chat = [...this.chat, line].slice(-CHAT.keep);
        this.broadcast({ t: 'chat', ...line });
        break;
      }
      case 'signal': {
        const coord = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && Math.abs(n) < 1e9;
        if (!(SIGNAL_KINDS as readonly unknown[]).includes(msg.kind) || !coord(msg.x) || !coord(msg.y)) break;
        if (msg.target !== undefined && !(coord(msg.target) && msg.target > 0)) break;
        const recent = (this.signalled.get(c.pid) ?? []).filter((at) => now - at < SIGNAL.windowMs);
        if (recent.length >= SIGNAL.burst) break;
        this.signalled.set(c.pid, [...recent, now]);
        this.broadcast({ t: 'signal', pid: c.pid, kind: msg.kind, x: msg.x, y: msg.y, ...(msg.target !== undefined ? { target: msg.target } : {}), at: now });
        break;
      }
      case 'leave':
        this.disconnect(connId);
        break;
    }
  }

  disconnect(connId: string): void {
    const c = this.conns.get(connId);
    if (!c) return;
    c.connected = false;
    this.conns.delete(connId);
    if (![...this.conns.values()].some((x) => x.pid === c.pid && x.connected)) this.control(c.pid, 'ghost');
    this.broadcast({ t: 'roster', ...this.roster() });
  }

  /** Server-injected control change, applied in the next closed tick so every client sees it at the same tick. */
  private control(pid: string, mode: 'live' | 'ghost'): void {
    if (!this.battle.heroes.some((h) => h.pid === pid)) return;
    this.injected[pid] = [...(this.injected[pid] ?? []), { t: 'control', mode }];
  }

  /** Server-injected command of a player the server itself plays (the enemy general), applied in the next closed tick. */
  inject(pid: string, cmd: Command): void {
    this.injected[pid] = [...(this.injected[pid] ?? []), cmd];
  }

  /** Closes the next tick: steps the authoritative sim and broadcasts the bundle. Call at 12 Hz. */
  step(now: number): void {
    // Silent connections turn into ghosts (they can come back with a new join → resync).
    for (const [id, c] of this.conns) if (c.connected && now - c.lastSeen > DROP_AFTER_MS) this.disconnect(id);
    if (this.battle.result !== 'running') return;
    const tick = this.battle.tick + 1;
    const inputs: TickInputs = {};
    for (const [pid, cmds] of Object.entries(this.injected)) inputs[pid] = cmds.slice();
    this.injected = {};
    const due = this.pending.get(tick);
    if (due) for (const [pid, cmds] of due) inputs[pid] = [...(inputs[pid] ?? []), ...cmds];
    this.pending.delete(tick);
    stepBattle(this.battle, inputs);
    if (Object.keys(inputs).length > 0) this.history.push({ tick, inputs });
    if (tick % HASH_EVERY === 0) {
      this.hashes.set(tick, hashState(this.battle));
      this.hashes.delete(tick - HASH_EVERY * 20);
    }
    this.broadcast({ t: 'tick', tick, inputs });
    // With the room's final state: a page whose own fight came out otherwise (it drifted, or missed the last
    // ticks) shows the room's end instead of a board frozen one nightmare short.
    if (this.battle.result !== 'running') this.broadcast({ t: 'end', result: this.battle.result, tick, snapshot: snapshot(this.battle) });
  }
}

/** Client side: applies bundles in order, sends inputs for tick + delay, reports hashes. */
export class LockstepClient {
  state: BattleState | null = null;
  /** Bundles received ahead of time (network reordering). */
  private buffer = new Map<number, TickInputs>();
  ended: 'won' | 'lost' | null = null;
  roster: { players: string[]; ghosts: string[] } = { players: [], ghosts: [] };
  /** Called after every simulated tick. */
  onTick: (s: BattleState) => void = () => undefined;
  /** What was said in the room (most recent last), and a hook for each new line. */
  chat: ChatLine[] = [];
  onChat: (line: ChatLine) => void = () => undefined;
  /** A signal from someone in the room (mine too, as the room echoes it): shown, never kept. */
  onSignal: (s: SignalLine) => void = () => undefined;
  /**
   * A welcome or resync replaced the state (a reconnect, a rebuilt lobby, a desync): the screen takes it as
   * it comes, even with no tick after it (a fight that ended while the player was away has none).
   */
  onReset: (state: BattleState) => void = () => undefined;
  resyncs = 0;
  /** The newest tick the server has closed (what my commands count from; ahead of the screen only while bundles wait reordered). */
  private latest = 0;

  constructor(
    readonly pid: string,
    private sendMsg: (m: ClientMsg) => void,
  ) {}

  join(sector: number): void {
    this.sendMsg({ t: 'join', pid: this.pid, sector });
  }

  say(text: string): void {
    this.sendMsg({ t: 'chat', text });
  }

  signal(s: Signal): void {
    this.sendMsg({ t: 'signal', ...s });
  }

  /** Ticks the server has closed that the screen hasn't played yet (each is 83 ms before a command shows). */
  get behind(): number {
    return this.state ? Math.max(0, this.latest - this.state.tick) : 0;
  }

  /** Queue my commands; they execute INPUT_DELAY ticks after the last tick I've seen. */
  input(cmds: Command[]): void {
    if (!this.state || cmds.length === 0) return;
    this.sendMsg({ t: 'input', tick: Math.max(this.state.tick, this.latest) + INPUT_DELAY, cmds });
  }

  onMessage(m: ServerMsg): void {
    switch (m.t) {
      case 'welcome':
      case 'resync': {
        this.state = m.snapshot;
        // A welcome is followed by the room's recent chat: start the list over.
        if (m.t === 'welcome') this.chat = [];
        if (m.t === 'resync') this.resyncs++;
        // A welcome may come from a rebuilt room (a lobby starts over at tick 0 when a friend joins): nothing
        // from the old one counts. A resync is the same room further on.
        if (m.t === 'welcome') {
          this.buffer.clear();
          this.latest = m.tick;
        } else this.latest = Math.max(this.latest, m.tick);
        for (const k of [...this.buffer.keys()]) if (k <= m.tick) this.buffer.delete(k);
        this.drain();
        if (this.state) this.onReset(this.state);
        break;
      }
      case 'tick':
        // Already past it (queued before a resync jumped ahead): nothing to do.
        if (this.state && m.tick <= this.state.tick) break;
        this.buffer.set(m.tick, m.inputs);
        this.latest = Math.max(this.latest, m.tick);
        this.drain();
        break;
      case 'roster':
        this.roster = { players: m.players, ghosts: m.ghosts };
        break;
      case 'end':
        this.ended = m.result;
        // The room's word on how it ended stands: a page that didn't get there itself takes the final state.
        if (m.snapshot && this.state && this.state.result !== m.result) {
          this.state = m.snapshot;
          this.buffer.clear();
          this.latest = Math.max(this.latest, m.tick);
          this.onReset(this.state);
        }
        break;
      case 'chat': {
        const line = { pid: m.pid, text: m.text, at: m.at };
        this.chat = [...this.chat, line].slice(-CHAT.keep);
        this.onChat(line);
        break;
      }
      case 'signal': {
        const { t: _t, ...line } = m;
        this.onSignal(line);
        break;
      }
      default:
        break;
    }
  }

  private drain(): void {
    const s = this.state;
    if (!s) return;
    for (;;) {
      const next = this.buffer.get(s.tick + 1);
      if (next === undefined) return;
      this.buffer.delete(s.tick + 1);
      stepBattle(s, next);
      if (s.tick % HASH_EVERY === 0) this.sendMsg({ t: 'hash', tick: s.tick, hash: hashState(s) });
      this.onTick(s);
    }
  }
}
